import React, { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown, Check, Building2 } from 'lucide-react';
import { useLocations } from '../../contexts/LocationContext';

export function LocationSelector() {
  const { locations, activeLocation, allLocationsMode, setActiveLocation, setAllLocationsMode } = useLocations();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (locations.length === 0) {
    return null;
  }

  const handleSelectAll = () => {
    setAllLocationsMode(true);
    setActiveLocation(null);
    setIsOpen(false);
  };

  const handleSelectLocation = (id: string) => {
    setActiveLocation(id);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 
                 rounded-lg transition-colors text-sm"
      >
        <MapPin className="w-4 h-4 text-blue-400" />
        <span className="text-slate-200 max-w-[150px] truncate">
          {allLocationsMode ? 'All Locations' : activeLocation?.name || 'Select Location'}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-64 bg-slate-800 border border-slate-700 
                      rounded-lg shadow-xl z-50 overflow-hidden">
          {/* All Locations Option */}
          <button
            onClick={handleSelectAll}
            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700/50 transition-colors ${
              allLocationsMode ? 'bg-blue-500/10' : ''
            }`}
          >
            <Building2 className="w-5 h-5 text-slate-400" />
            <div className="flex-1 text-left">
              <p className="text-sm text-slate-200">All Locations</p>
              <p className="text-xs text-slate-500">View combined data</p>
            </div>
            {allLocationsMode && <Check className="w-4 h-4 text-blue-400" />}
          </button>

          <div className="border-t border-slate-700" />

          {/* Individual Locations */}
          <div className="max-h-64 overflow-y-auto">
            {locations.map((location) => (
              <button
                key={location.id}
                onClick={() => handleSelectLocation(location.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700/50 transition-colors ${
                  activeLocation?.id === location.id ? 'bg-blue-500/10' : ''
                }`}
              >
                <MapPin className={`w-5 h-5 ${
                  location.type === 'main' ? 'text-blue-400' :
                  location.type === 'branch' ? 'text-green-400' : 'text-purple-400'
                }`} />
                <div className="flex-1 text-left">
                  <p className="text-sm text-slate-200">{location.name}</p>
                  <p className="text-xs text-slate-500">
                    {location.providerNumbers.length} provider{location.providerNumbers.length !== 1 ? 's' : ''}
                  </p>
                </div>
                {activeLocation?.id === location.id && <Check className="w-4 h-4 text-blue-400" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
